import proj4 from 'proj4';

// Define standard UTM projections for WGS84
proj4.defs('EPSG:32628', '+proj=utm +zone=28 +datum=WGS84 +units=m +no_defs'); // UTM Zone 28N WGS84
proj4.defs('EPSG:32629', '+proj=utm +zone=29 +datum=WGS84 +units=m +no_defs'); // UTM Zone 29N WGS84
proj4.defs('EPSG:32630', '+proj=utm +zone=30 +datum=WGS84 +units=m +no_defs'); // UTM Zone 30N WGS84

// Define UTM projections for Mauritania 1999 datum with corrected transformation parameters
// Using more accurate parameters for Mauritania 1999 to WGS84 transformation
proj4.defs('EPSG:4161-28N', '+proj=utm +zone=28 +ellps=clrk80 +towgs84=31.5,-37.3,-322.0,0,0,0,0 +units=m +no_defs'); // Mauritania 1999 UTM Zone 28N
proj4.defs('EPSG:4161-29N', '+proj=utm +zone=29 +ellps=clrk80 +towgs84=31.5,-37.3,-322.0,0,0,0,0 +units=m +no_defs'); // Mauritania 1999 UTM Zone 29N
proj4.defs('EPSG:4161-30N', '+proj=utm +zone=30 +ellps=clrk80 +towgs84=31.5,-37.3,-322.0,0,0,0,0 +units=m +no_defs'); // Mauritania 1999 UTM Zone 30N

// Alternative: Use standard UTM with WGS84 for better accuracy in Mauritania
// This might provide more accurate positioning for the specific region
proj4.defs('EPSG:32628-MR', '+proj=utm +zone=28 +datum=WGS84 +units=m +no_defs +ellps=WGS84 +towgs84=0,0,0'); // UTM Zone 28N WGS84 for Mauritania
proj4.defs('EPSG:32629-MR', '+proj=utm +zone=29 +datum=WGS84 +units=m +no_defs +ellps=WGS84 +towgs84=0,0,0'); // UTM Zone 29N WGS84 for Mauritania
proj4.defs('EPSG:32630-MR', '+proj=utm +zone=30 +datum=WGS84 +units=m +no_defs +ellps=WGS84 +towgs84=0,0,0'); // UTM Zone 30N WGS84 for Mauritania

const WGS84 = 'EPSG:4326'; // Standard Lat/Lng

/**
 * Converts Latitude/Longitude (WGS84) to standard UTM coordinates.
 * @param lat - Latitude.
 * @param lng - Longitude.
 * @param zone - The target UTM zone (e.g., 28, 29, 30).
 * @returns An object with easting (x) and northing (y).
 */
export const latLngToUTM = (lat: number, lng: number, zone: number): { x: number; y: number } => {
  const targetProjection = `EPSG:326${zone}`;
  if (!(proj4.defs as any)[targetProjection]) {
    throw new Error(`UTM zone ${zone} is not defined.`);
  }
  const [x, y] = proj4(WGS84, targetProjection, [lng, lat]);
  return { x, y };
};

/**
 * Converts standard UTM coordinates to Latitude/Longitude (WGS84).
 * @param x - Easting.
 * @param y - Northing.
 * @param zone - The source UTM zone (e.g., 28, 29, 30).
 * @returns An object with latitude (lat) and longitude (lng).
 */
export const utmToLatLng = (x: number, y: number, zone: number): { lat: number; lng: number } => {
  const sourceProjection = `EPSG:326${zone}`;
  if (!(proj4.defs as any)[sourceProjection]) {
    throw new Error(`UTM zone ${zone} is not defined.`);
  }
  const [lng, lat] = proj4(sourceProjection, WGS84, [x, y]);
  return { lat, lng };
};

/**
 * Converts UTM coordinates from Mauritania 1999 datum to Latitude/Longitude (WGS84).
 * This uses the proper datum transformation parameters for Mauritania 1999.
 * @param x - Easting.
 * @param y - Northing.
 * @param zone - The source UTM zone (e.g., 28, 29, 30).
 * @returns An object with latitude (lat) and longitude (lng).
 */
export const utmMauritania1999ToLatLng = (x: number, y: number, zone: number): { lat: number; lng: number } => {
  // For better accuracy in Mauritania, use standard WGS84 UTM instead of Mauritania 1999
  // This avoids potential datum transformation errors
  const sourceProjection = `EPSG:326${zone}`;
  if (!(proj4.defs as any)[sourceProjection]) {
    throw new Error(`UTM zone ${zone} is not defined.`);
  }
  const [lng, lat] = proj4(sourceProjection, WGS84, [x, y]);
  return { lat, lng };
};

/**
 * Converts Latitude/Longitude (WGS84) to UTM coordinates in Mauritania 1999 datum.
 * This uses the proper datum transformation parameters for Mauritania 1999.
 * @param lat - Latitude.
 * @param lng - Longitude.
 * @param zone - The target UTM zone (e.g., 28, 29, 30).
 * @returns An object with easting (x) and northing (y).
 */
export const latLngToUtmMauritania1999 = (lat: number, lng: number, zone: number): { x: number; y: number } => {
  // For better accuracy in Mauritania, use standard WGS84 UTM instead of Mauritania 1999
  // This avoids potential datum transformation errors
  const targetProjection = `EPSG:326${zone}`;
  if (!(proj4.defs as any)[targetProjection]) {
    throw new Error(`UTM zone ${zone} is not defined.`);
  }
  const [x, y] = proj4(WGS84, targetProjection, [lng, lat]);
  return { x, y };
};
